import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {ILogger} from '@/utils/ILogger';
import {LogdownLogger} from '@/utils/LogdownLogger';

import {FeedInfo} from '@/model/system/update/Feed';
import {ReleaseInfo} from '@/model/system/update/Release';
import {OsInfo} from '@/model/system/OsInfo';

import {ISystemUpdateService} from '@/services/system/IUpdate.service';

@Module({name: 'SystemUpdateStore', namespaced: true})
export default class SystemUpdateStore extends VuexModule {
  private logger: ILogger = new LogdownLogger('SystemUpdateStore');

  private feedInfo: FeedInfo | null = null;

  private releaseInfo: ReleaseInfo | null = null;

  private updateAvailable: boolean | null = null;

  private isUpgradable: boolean | null = null;

  @Inject(Symbol.for('ISystemUpdateService'))
  private systemUpdateService!: ISystemUpdateService;

  /* FEED */
  @Action({commit: 'setFeedInfo', rawError: true})
  public async fetchFeedInfo(fetchArgs: any) {
    const path: string = fetchArgs.path;
    const args: Map<string, string> = fetchArgs.args;

    this.logger.trace('Fetching feed info', path, args);
    return await this.systemUpdateService.getFeedInfo(path, args);
  }

  @Mutation
  public setFeedInfo(feedInfo: FeedInfo) {
    this.feedInfo = feedInfo;
  }

  get FeedInfo(): FeedInfo | null {
    return this.feedInfo;
  }


  /* RELEASE */
  @Action({commit: 'setReleaseInfo', rawError: true})
  public async fetchReleaseInfo(fetchArgs: any) {
    const path: string = fetchArgs.path;
    const args: Map<string, string> = fetchArgs.args;

    this.logger.trace('Fetching release info', path, args);
    return await this.systemUpdateService.getReleaseInfo(path, args);
  }

  @Mutation
  public setReleaseInfo(releaseInfo: ReleaseInfo) {
    this.releaseInfo = releaseInfo;
  }

  get ReleaseInfo(): ReleaseInfo | null {
    return this.releaseInfo;
  }


  /* UPDATE AVAILABLE */
  @Action({commit: 'setUpdateAvailable', rawError: true})
  public async checkUpdateAvailable(checkArgs: any) {
    const osInfo: OsInfo = checkArgs.osInfo;
    const repository: string = checkArgs.repository;
    const channel: string = checkArgs.channel;

    this.logger.trace('Check update available', osInfo, channel);

    const args = new Map<string, string>();
    args.set('channel', channel);
    args.set('version', osInfo.VersionNormalized);
    args.set('machine', osInfo.Machine);

    await this.fetchFeedInfo({
      path: repository,
      args,
    });
    if (!this.FeedInfo) {
      throw new Error('Could not recover feed info');
    }

    this.logger.trace('Looking for channel', channel);
    const channelInfo = this.FeedInfo.channels.find((c) => c.name === channel);
    if (!channelInfo) {
      throw new Error(`Channel '${channel}' could not be found`);
    }

    this.logger.trace('Looking for a release for machine', osInfo.Machine);
    const releaseInfo = channelInfo.releases.find((r) => r.machines.includes(osInfo.Machine));
    if (!releaseInfo) {
      throw new Error(`No release available for machine '${osInfo.Machine}'`);
    }

    await this.fetchReleaseInfo({
      path: releaseInfo.path,
      args,
    });
    if (!this.ReleaseInfo) {
      throw new Error('Could not recover release info');
    }

    this.context.commit(
      'setIsUpgradable',
      await this.systemUpdateService.isUpgradable(osInfo.VersionNormalized, this.ReleaseInfo),
    );

    return this.systemUpdateService.isUpdateAvailable(this.ReleaseInfo, osInfo.VersionNormalized);
  }

  @Mutation
  public setUpdateAvailable(available: boolean) {
    this.updateAvailable = available;
  }

  get UpdateAvailable(): boolean | null {
    return this.updateAvailable;
  }

  @Mutation
  public setIsUpgradable(upgradable: boolean) {
    this.isUpgradable = upgradable;
  }

  get IsUpgradable(): boolean | null {
    return this.isUpgradable;
  }
}
